import * as React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import { getMatchResearch } from '../../Api';
import { MatchResearchDto } from '../../models/dtos/MatchBriefDto';
import { formatAiUsd } from '../ai/AiShared';
import MatchBriefExecutionDialog from './MatchBriefExecutionDialog';

export const matchResearchTitle = (r: Pick<MatchResearchDto, 'match'>) =>
    r.match.home && r.match.away ? `${r.match.home} – ${r.match.away}` : `Match ${r.match.id}`;

/** "Fixture confirmed" or "Fixture not found" (the researcher's match_confirmed). */
export const matchResearchConfirmedChip = (confirmed: boolean) =>
    <Chip size="small" color={confirmed ? 'success' : 'warning'} variant="outlined" label={confirmed ? 'Fixture confirmed' : 'Fixture not found'} />;

const host = (url: string) => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

/**
 * One web research run of a match (qbet_match_research), read-only: what the researcher found — each finding with its
 * status, team, source and date — the hub run it came from and the invocation that asked for it.
 */
export default function MatchResearchDialog({ researchId, onClose, showMatchLink = true }: { researchId: number | null; onClose: () => void; showMatchLink?: boolean }) {
    const [research, setResearch] = React.useState<MatchResearchDto | null>(null);
    const [failed, setFailed] = React.useState(false);
    const [executionOpen, setExecutionOpen] = React.useState(false);

    React.useEffect(() => {
        setResearch(null);
        setFailed(false);
        if (researchId == null) return;
        let cancelled = false;
        getMatchResearch(researchId).then(result => {
            if (cancelled) return;
            setResearch(result);
            setFailed(result == null);
        });
        return () => { cancelled = true; };
    }, [researchId]);

    const findings = research?.findings?.findings ?? [];

    return (
        <>
            <Dialog open={researchId != null} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <span>Research #{researchId}{research ? ` · ${matchResearchTitle(research)}` : ''}</span>
                        {research && matchResearchConfirmedChip(research.match_confirmed)}
                    </Stack>
                    {research && (
                        <Typography variant="body2" color="text.secondary">
                            Researched {new Date(research.researched_at).toLocaleString()}
                            {research.findings?.competition ? ` · ${research.findings.competition}` : research.match.league ? ` · ${research.match.league}` : ''}
                            {research.match.start ? ` · kick-off ${new Date(research.match.start).toLocaleString()}` : ''}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {researchId != null && !research && !failed && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
                    {failed && <Alert severity="error">The research could not be loaded.</Alert>}
                    {research && (
                        <Stack gap={2}>
                            <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-end">
                                <Meta label="Findings" value={String(findings.length)} />
                                <Meta label="AI cost" value={formatAiUsd(research.cost_usd)} />
                                <Meta label="Research run" value={research.run_id
                                    ? <Link component={RouterLink} to={`/ai/runs/${research.run_id}`} onClick={onClose}>{research.run_id.slice(0, 8)}…</Link>
                                    : '—'} />
                                {showMatchLink && <Meta label="Match" value={<Link component={RouterLink} to={`/matches/${research.match.id}`} onClick={onClose}>{research.match.id}</Link>} />}
                                {research.execution_id && <Button size="small" variant="outlined" onClick={() => setExecutionOpen(true)}>Open invocation #{research.execution_id}</Button>}
                            </Stack>
                            {!research.match_confirmed && (
                                <Alert severity="warning">The researcher couldn't confirm this fixture, so it reported nothing. A brief doesn't reuse this research.</Alert>
                            )}
                            {research.match_confirmed && findings.length === 0 && (
                                <Alert severity="info">The fixture was found, but nothing notable: no absences, rotation or motivation gap worth reporting.</Alert>
                            )}
                            {findings.length > 0 && (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Team</TableCell>
                                            <TableCell>Claim</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell>Published</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {findings.map((f, i) => (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    <Chip size="small" variant={f.status === 'confirmed' ? 'filled' : 'outlined'}
                                                        color={f.status === 'confirmed' ? 'success' : 'warning'} label={f.status ?? '—'} />
                                                </TableCell>
                                                <TableCell>{f.type ?? '—'}</TableCell>
                                                <TableCell>{f.team === 'home' ? research.match.home ?? 'home' : f.team === 'away' ? research.match.away ?? 'away' : f.team ?? '—'}</TableCell>
                                                <TableCell sx={{ minWidth: 280 }}>{f.claim}</TableCell>
                                                <TableCell>
                                                    {f.source && /^https?:\/\//i.test(f.source)
                                                        ? <Link href={f.source} target="_blank" rel="noopener noreferrer">{host(f.source)}</Link>
                                                        : f.source ?? '—'}
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.published ?? '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            <Accordion disableGutters variant="outlined">
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">Researcher answer (JSON)</Typography></AccordionSummary>
                                <AccordionDetails>
                                    <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(research.findings, null, 2)}</Box>
                                </AccordionDetails>
                            </Accordion>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
            </Dialog>
            <MatchBriefExecutionDialog executionId={executionOpen && research?.execution_id ? research.execution_id : null} onClose={() => setExecutionOpen(false)} />
        </>
    );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" component="div">{value}</Typography>
        </Box>
    );
}
